import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { Check, Zap, Shield, Crown } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  features: string;
}

const Plans: React.FC = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(API.getSubscriptionPlans());
        setPlans(response.data);
      } catch (err) {
        console.error('Failed to load plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleUpgrade = async (planId: string) => {
    if (!user) return;
    setUpgrading(planId);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(API.upgradeSubscription(user.id), { new_plan_id: planId }, { headers });
      alert('Subscription updated successfully!');
      window.location.href = '/subscriptions';
    } catch (err) {
      console.error('Failed to upgrade:', err);
      alert('Failed to update subscription.');
    } finally {
      setUpgrading(null);
    }
  };

  const getIcon = (name: string) => {
    if (name.toLowerCase().includes('pro')) return <Zap size={24} />;
    if (name.toLowerCase().includes('enterprise')) return <Crown size={24} />;
    return <Shield size={24} />;
  };

  if (loading) return <div>Loading plans...</div>;

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h1 className="page-title" style={{ marginBottom: '12px' }}>Select Your Plan</h1>
        <p style={{ color: '#64748B', fontSize: '18px' }}>Choose the best plan for your tax refund needs</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', maxWidth: '1200px', margin: '0 auto' }}>
        {plans.map((plan) => (
          <div key={plan.id} className="section-card" style={{ display: 'flex', flexDirection: 'column', padding: '40px', position: 'relative', overflow: 'hidden' }}>
            {plan.name.toLowerCase().includes('pro') && (
              <div style={{ position: 'absolute', top: '12px', right: '-35px', background: '#2563EB', color: '#fff', padding: '4px 40px', transform: 'rotate(45deg)', fontSize: '12px', fontWeight: 700 }}>
                POPULAR
              </div>
            )}
            <div style={{ color: '#2563EB', marginBottom: '24px' }}>
              {getIcon(plan.name)}
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{plan.name}</h2>
            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px', minHeight: '40px' }}>{plan.description}</p>
            <div style={{ marginBottom: '32px' }}>
              <span style={{ fontSize: '40px', fontWeight: 800 }}>£{plan.price}</span>
              <span style={{ color: '#64748B' }}>/mo</span>
            </div>
            
            <div style={{ flex: 1, marginBottom: '40px' }}>
              {(plan.features || '').split(',').map((feature, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', fontSize: '14px' }}>
                  <Check size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                  <span>{feature.trim()}</span>
                </div>
              ))}
            </div>

            <button 
              className={`btn ${plan.name.toLowerCase().includes('pro') ? 'btn-primary' : 'btn-outline'}`}
              style={{ width: '100%' }}
              onClick={() => handleUpgrade(plan.id)}
              disabled={upgrading !== null}
            >
              {upgrading === plan.id ? 'Processing...' : 'Select Plan'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Plans;
